/**
 * The icon registry (DESIGN §4.8, ADR-0019): every icon the product uses, under a product
 * name. Pages import `Icon.Visits`, never `lucide-react` directly, so a glyph is swapped in one
 * place and only the icons named here are bundled (Lucide tree-shakes per icon).
 *
 * Rendering rules live in the primitives, not here: 16 px in controls and tables, 18 px in the
 * navigation, a 1.5 px stroke, `currentColor` only, and never an icon without a visible label
 * or an `aria-label` (DESIGN §4.8). Icons are decorative to assistive technology by default
 * (`aria-hidden`); the label beside them carries the meaning.
 */

import {
  Activity,
  AppWindow,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpRight,
  Bell,
  BellRing,
  Bot,
  Building2,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleDashed,
  CircleHelp,
  CircleX,
  Copy,
  Crosshair,
  Download,
  Ellipsis,
  Eye,
  EyeOff,
  Globe,
  Hexagon,
  Info,
  Keyboard,
  KeyRound,
  LayoutDashboard,
  Link2,
  Map as MapGlyph,
  ListFilter,
  LogOut,
  Menu,
  Minus,
  Monitor,
  MousePointer2,
  Move,
  MonitorSmartphone,
  PanelLeftClose,
  PanelLeftOpen,
  Pentagon,
  Plus,
  Radio,
  RotateCw,
  Rows3,
  Search,
  Send,
  Server,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestionMark,
  Signal,
  Smartphone,
  Spline,
  SunMoon,
  Table2,
  Tablet,
  Trash2,
  TriangleAlert,
  Tv,
  Upload,
  UserCog,
  UserPlus,
  VenetianMask,
  Wifi,
  Workflow,
  X,
  type LucideIcon,
  Database,
  Bookmark,
  Funnel,
  Gauge,
  GitCompareArrows,
  HardDrive,
  Pencil,
  QrCode,
  Repeat2,
  Share2,
  StickyNote,
  Target,
} from 'lucide-react';

export const Icon = {
  // Navigation (DESIGN §9.1)
  Overview: LayoutDashboard,
  Visits: Rows3,
  Geography: Globe,
  Breakdowns: ChartColumn,
  Inference: Workflow,
  Detection: ShieldAlert,
  Geofences: Hexagon,
  Alerts: BellRing,
  Health: Activity,
  Account: Settings,
  // Shell and actions
  Search,
  Help: CircleHelp,
  Shortcuts: Keyboard,
  Collapse: PanelLeftClose,
  Expand: PanelLeftOpen,
  Menu,
  Close: X,
  Chevron: ChevronDown,
  Next: ChevronRight,
  Previous: ChevronLeft,
  Back: ArrowLeft,
  More: Ellipsis,
  Filter: ListFilter,
  Add: Plus,
  Copy,
  Check,
  Download,
  Upload,
  External: ArrowUpRight,
  Calendar: CalendarDays,
  Link: Link2,
  Table: Table2,
  Theme: SunMoon,
  SignOut: LogOut,
  Locate: Crosshair,
  // State markers
  Info,
  Warn: TriangleAlert,
  Error: CircleX,
  Up: ArrowUp,
  Down: ArrowDown,
  Flat: Minus,
  Live: Radio,
  // Devices and networks, beside their words in tables and lists (DESIGN §12 E22). Generic
  // glyphs only: a brand's logo is its trademark, and Lucide ships none.
  DeviceMobile: Smartphone,
  DeviceTablet: Tablet,
  DeviceDesktop: Monitor,
  DeviceTv: Tv,
  DeviceServer: Server,
  DeviceBot: Bot,
  InApp: AppWindow,
  Browser: Globe,
  NetMobile: Signal,
  NetBroadband: Wifi,
  NetDatacenter: Server,
  NetVpn: ShieldQuestionMark,
  NetTor: VenetianMask,
  NetBusiness: Building2,
  Unknown: CircleDashed,
  // Settings (DESIGN §10.8)
  Show: Eye,
  Hide: EyeOff,
  Invite: UserPlus,
  Profile: UserCog,
  Security: KeyRound,
  Verified: ShieldCheck,
  Sessions: MonitorSmartphone,
  System: Server,
  // Geofences and alerts (DESIGN §16, M6)
  ShapePolygon: Pentagon,
  ShapeCircle: Circle,
  ShapeRegion: MapGlyph,
  ToolSelect: MousePointer2,
  ToolEdit: Spline,
  ToolMove: Move,
  Delete: Trash2,
  Notifications: Bell,
  Retry: RotateCw,
  Send,
  // System health (M7)
  Databases: Database,
  Data: HardDrive,
  Limits: Gauge,
  // M7.6: Sources, Returning visitors, Capture quality
  Sources: Share2,
  Returning: Repeat2,
  Capture: Funnel,
  // M7.7: Compare, annotations, saved views, the link builder
  Compare: GitCompareArrows,
  Note: StickyNote,
  Edit: Pencil,
  SaveView: Bookmark,
  QrCode,
  // M8: ground truth (DESIGN §16 M8)
  GroundTruth: Target,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof Icon;
